import { AudioNote, CalendarEvent, ApiAudioNote } from '@highlight/common'
import { GlobalEvents } from '@highlight/ipc'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { useSetSelectedAudioNoteId, useSetSidePanelOpen } from '@renderer/stores/side-panel'
import { useMutation, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSelectedAudioNote } from './useSelectedAudioNote'
import { useAuth } from '@renderer/chat/providers'
import { v1Client } from '@renderer/chat'
import { PAGINATION_LIMIT } from '@renderer/utils/constants'
import { useMemo, useCallback } from 'react'
import { useSharedWithMe } from '@renderer/windows/features/memory/hooks/queries-shared'
import { useAudioNoteSettings } from '@renderer/chat/hooks/useAudioNoteSettings'
import { isDayToday } from '@shared/utils/DateUtils'

export const AUDIO_NOTES_QUERY_KEY = 'audio-notes'
export const SHARED_ITEMS_QUERY_KEY = 'shared-items'

/** OpenAPI client types lag the list-spec filters (`view`, `calendarEventId`, ranges). */
type AudioNotesListQuery = {
  limit?: number
  nextCursor?: string
  view?: 'metadata' | 'full'
  calendarEventId?: string
  'startedAt[gte]'?: string
  'startedAt[lte]'?: string
}

type ApiAudioNoteRow = ApiAudioNote & { calendarEventId?: string | null }

function mapApiAudioNoteToClient(audioNote: ApiAudioNoteRow): AudioNote {
  const calendarEventId = audioNote.calendarEventId ?? audioNote.meeting?.calendarEventId
  const meeting = audioNote.meeting
    ? { ...audioNote.meeting, ...(calendarEventId ? { calendarEventId } : {}) }
    : undefined

  return {
    id: audioNote.id,
    userId: audioNote.userId || '',
    title: audioNote.title || '',
    summary: audioNote.summary || '',
    startedAt: new Date(audioNote.startedAt ?? Date.now()),
    endedAt: new Date(audioNote.endedAt ?? Date.now()),
    meeting,
    shareLink: audioNote.shareLink,
    chatConversationId: audioNote.chatConversationId,
  }
}

/** Drop transcript corpora from local IPC rows so Sidepanel join caches stay light. */
function toMetadataNote(note: AudioNote): AudioNote {
  if (!note.transcriptEntries?.length) {
    return note
  }
  return { ...note, transcriptEntries: undefined }
}

function startOfLocalDayIso(): string {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today.toISOString()
}

async function fetchAudioNotesList(
  getAuthToken: () => Promise<string>,
  query: AudioNotesListQuery,
): Promise<Array<AudioNote>> {
  // List-spec filters (`view`, `calendarEventId`, `startedAt[gte]`) exist on the
  // gateway but lag the generated OpenAPI client types — cast until regenerated.
  const response = await v1Client.GET('/audio-notes', {
    params: {
      query: query as { limit?: number; nextCursor?: string; includeTranscript?: boolean },
    },
    headers: {
      Authorization: `Bearer ${await getAuthToken()}`,
    },
  })

  if (response.error) {
    throw new Error('Network response was not ok')
  }

  const source: Array<ApiAudioNoteRow> = Array.isArray(response.data?.data)
    ? (response.data.data as Array<ApiAudioNoteRow>)
    : []
  return source.map(mapApiAudioNoteToClient).sort((a, b) => b.endedAt.getTime() - a.endedAt.getTime())
}

function matchesLegacyMeetingLink(audioNote: AudioNote, meeting: CalendarEvent): boolean {
  return (
    (!!meeting.conferenceData?.uri && audioNote.meeting?.url?.split('?')[0] === meeting.conferenceData.uri) ||
    !!audioNote.meeting?.windowTitle?.includes(meeting.title) ||
    audioNote.title === meeting.title
  )
}

function useAudioNotes(options?: { enabled?: boolean }) {
  const { getAuthToken } = useAuth()
  const { isUsingLocalStorage } = useAudioNoteSettings()
  const enabled = options?.enabled ?? true
  const sharedItemsQuery = useSharedWithMe()

  const query = useInfiniteQuery({
    queryKey: [AUDIO_NOTES_QUERY_KEY, isUsingLocalStorage],
    enabled,
    queryFn: async ({ pageParam }: { pageParam: string }) => {
      // When cloud sync is enabled, use the API
      if (!isUsingLocalStorage) {
        const response = await v1Client.GET('/audio-notes', {
          params: {
            query: {
              limit: PAGINATION_LIMIT,
              nextCursor: pageParam,
            },
          },
          headers: {
            Authorization: `Bearer ${await getAuthToken()}`,
          },
        })

        if (response.error) {
          throw new Error('Network response was not ok')
        }

        const data = response.data ?? { data: [], nextCursor: undefined as string | undefined }

        const source: Array<ApiAudioNoteRow> = Array.isArray(data.data) ? (data.data as Array<ApiAudioNoteRow>) : []
        const mappedData = source.map(mapApiAudioNoteToClient).sort((a, b) => b.endedAt.getTime() - a.endedAt.getTime())

        return {
          data: mappedData,
          nextCursor: data?.nextCursor,
        }
      } else {
        // When cloud sync is disabled, fetch all local audio notes
        // and handle pagination client-side
        const raw = await AudioNoteIpcRenderer.getAudioNotes()
        const allConversations: AudioNote[] = Array.isArray(raw) ? (raw as AudioNote[]) : []

        // Parse pageParam as offset (empty string means 0)
        const offset = pageParam ? parseInt(pageParam, 10) : 0

        // Slice the data based on offset and limit
        const safeOffset = Number.isFinite(offset) && offset >= 0 ? offset : 0
        const paginatedData = allConversations
          .sort((a, b) => b.endedAt.getTime() - a.endedAt.getTime())
          .slice(safeOffset, safeOffset + PAGINATION_LIMIT)

        // Calculate next cursor
        const hasMore = safeOffset + PAGINATION_LIMIT < (allConversations?.length ?? 0)
        const nextCursor = hasMore ? String(safeOffset + PAGINATION_LIMIT) : undefined

        return {
          data: paginatedData as Array<AudioNote>,
          nextCursor: nextCursor,
        }
      }
    },
    initialPageParam: '',
    getNextPageParam: (lastPage: { nextCursor?: string }) => lastPage.nextCursor,
    staleTime: 5 * 60 * 1000,
  })

  // Mapped in its own memo so shared-item identities survive audio-page
  // appends/refetches — downstream consumers memo on object identity, and
  // fresh objects would re-render every row.
  const sharedAudioNotes = useMemo(
    () =>
      sharedItemsQuery.data?.map(
        (item) =>
          ({
            ...item,
            endedAt: new Date(item.unifiedUpdatedAt),
          }) as AudioNote & { isShared: true },
      ) ?? [],
    [sharedItemsQuery.data],
  )

  const audioNotes: AudioNote[] = useMemo(() => {
    const localAudioNotes = query.data?.pages.flatMap((page) => (Array.isArray(page?.data) ? page.data : [])) ?? []

    // Single pass: dedupe by id and decorate with a numeric sort key. Dedupe
    // guards against page-boundary overlap (the keyset cursor is updatedAt-
    // based, so a note re-summarized mid-refetch can land in two pages) and
    // own/shared collisions — duplicate ids break virtualized row keys.
    // Decorating up front keeps Date allocations O(n) instead of O(n log n)
    // inside the sort comparator.
    const seenIds = new Set<string>()
    const decorated: Array<{ ts: number; note: AudioNote }> = []
    const push = (note: AudioNote) => {
      if (seenIds.has(note.id)) return
      seenIds.add(note.id)
      decorated.push({ ts: new Date(note.endedAt).getTime(), note })
    }
    for (const note of localAudioNotes) push(note)
    for (const note of sharedAudioNotes) push(note)

    decorated.sort((a, b) => b.ts - a.ts)
    return decorated.map((entry) => entry.note)
  }, [query.data, sharedAudioNotes])

  const fetchNextPage = useCallback(async () => {
    const promises = []
    if (query.hasNextPage) {
      promises.push(query.fetchNextPage())
    }
    if (sharedItemsQuery.hasNextPage) {
      promises.push(sharedItemsQuery.fetchNextPage())
    }
    if (promises.length > 0) {
      await Promise.all(promises)
    }
  }, [query, sharedItemsQuery])

  return useMemo(() => {
    return {
      ...query,
      data: audioNotes,
      hasNextPage: query.hasNextPage || sharedItemsQuery.hasNextPage,
      isLoading: query.isLoading || sharedItemsQuery.isLoading,
      isFetchingNextPage: query.isFetchingNextPage || sharedItemsQuery.isFetchingNextPage,
      fetchNextPage,
    }
  }, [audioNotes, query, sharedItemsQuery, fetchNextPage])
}

function useAudioNoteById(audioNoteId: string | undefined) {
  return useQuery({
    queryKey: ['audio-note', audioNoteId],
    queryFn: async () => {
      if (!audioNoteId) return null
      const result = await AudioNoteIpcRenderer.getAudioNote(audioNoteId)
      // Main answers a missing note with undefined, which TanStack Query
      // rejects and retries — a note that doesn't exist would spin for the
      // whole retry schedule before settling. Null settles it at once. A
      // lookup that *failed* rejects instead, so it retries and is never
      // cached as missing.
      return (result as AudioNote | undefined) ?? null
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
    enabled: !!audioNoteId,
  })
}

function useDeleteAudioNote() {
  const { data: selectedAudioNote } = useSelectedAudioNote()
  const setSelectedAudioNoteId = useSetSelectedAudioNoteId()
  const setSidePanelOpen = useSetSidePanelOpen()
  const { isUsingLocalStorage } = useAudioNoteSettings()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['delete-audio-note'],
    mutationFn: async (audioNoteId: AudioNote['id']) => {
      if (!audioNoteId) return

      await AudioNoteIpcRenderer.deleteAudioNote(audioNoteId)

      return audioNoteId
    },
    onMutate: async (audioNoteId: AudioNote['id']) => {
      if (!audioNoteId) return

      // Cancel any outgoing refetches
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [AUDIO_NOTES_QUERY_KEY, isUsingLocalStorage] }),
        queryClient.cancelQueries({ queryKey: [SHARED_ITEMS_QUERY_KEY] }),
      ])

      // Snapshot the previous value
      const previousAudioNotes = queryClient.getQueryData([AUDIO_NOTES_QUERY_KEY, isUsingLocalStorage])
      const previousSharedItems = queryClient.getQueryData([SHARED_ITEMS_QUERY_KEY])

      // Optimistically update the cache by removing the deleted audio note
      queryClient.setQueryData([AUDIO_NOTES_QUERY_KEY, isUsingLocalStorage], (old: any) => {
        if (!old) return old

        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.filter((audioNote: AudioNote) => audioNote.id !== audioNoteId),
          })),
        }
      })

      // Do the same for shared items
      queryClient.setQueryData([SHARED_ITEMS_QUERY_KEY], (old: any) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.filter((audioNote: AudioNote & { isShared: true }) => audioNote.id !== audioNoteId),
          })),
        }
      })

      // Return a context object with the snapshotted value
      return { previousAudioNotes, previousSharedItems }
    },
    onSuccess: (audioNoteId) => {
      if (selectedAudioNote?.id === audioNoteId) {
        setSidePanelOpen(false)
        setSelectedAudioNoteId(null)
      }

      // Cross-renderer: the overlay's history feed lists this note.
      GlobalEvents.emit('audio-notes:changed')

      console.log('Deleted audio note:', audioNoteId)
    },
    onError: (error, audioNoteId, context) => {
      // Rollback the optimistic update
      if (context?.previousAudioNotes) {
        queryClient.setQueryData([AUDIO_NOTES_QUERY_KEY, isUsingLocalStorage], context.previousAudioNotes)
      }
      if (context?.previousSharedItems) {
        queryClient.setQueryData([SHARED_ITEMS_QUERY_KEY], context.previousSharedItems)
      }

      console.error('Failure to delete audio note:', audioNoteId, error)
    },
  })
}

function useBulkDeleteAudioNotes() {
  const { data: selectedAudioNote } = useSelectedAudioNote()
  const setSelectedAudioNoteId = useSetSelectedAudioNoteId()
  const setSidePanelOpen = useSetSidePanelOpen()
  const { isUsingLocalStorage } = useAudioNoteSettings()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['delete-audio-note'],
    mutationFn: async (audioNoteIds: Array<AudioNote['id']>) => {
      if (!audioNoteIds || audioNoteIds.length === 0) return

      for (const audioNoteId of audioNoteIds) {
        await AudioNoteIpcRenderer.deleteAudioNote(audioNoteId)
      }

      return audioNoteIds
    },
    onMutate: async (audioNoteIds: Array<AudioNote['id']>) => {
      if (!audioNoteIds || audioNoteIds.length === 0) return

      // Cancel any outgoing refetches
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [AUDIO_NOTES_QUERY_KEY, isUsingLocalStorage] }),
        queryClient.cancelQueries({ queryKey: [SHARED_ITEMS_QUERY_KEY] }),
      ])

      // Snapshot the previous value
      const previousAudioNotes = queryClient.getQueryData([AUDIO_NOTES_QUERY_KEY, isUsingLocalStorage])
      const previousSharedItems = queryClient.getQueryData([SHARED_ITEMS_QUERY_KEY])

      // Optimistically update the cache by removing the deleted audio notes
      queryClient.setQueryData([AUDIO_NOTES_QUERY_KEY, isUsingLocalStorage], (old: any) => {
        if (!old) return old

        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.filter((audioNote: AudioNote) => !audioNoteIds.includes(audioNote.id)),
          })),
        }
      })

      // Do the same for shared items
      queryClient.setQueryData([SHARED_ITEMS_QUERY_KEY], (old: any) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.filter((audioNote: AudioNote & { isShared: true }) => !audioNoteIds.includes(audioNote.id)),
          })),
        }
      })

      // Return a context object with the snapshotted value
      return { previousAudioNotes, previousSharedItems }
    },
    onSuccess: (audioNoteIds) => {
      if (!audioNoteIds || audioNoteIds.length === 0) return

      for (const audioNoteId of audioNoteIds) {
        if (selectedAudioNote?.id === audioNoteId) {
          setSidePanelOpen(false)
          setSelectedAudioNoteId(null)
        }
      }

      // Cross-renderer: the overlay's history feed lists these notes.
      GlobalEvents.emit('audio-notes:changed')

      console.log('Deleted audio notes:', audioNoteIds)
    },
    onError: (error, audioNoteIds, context) => {
      // Rollback the optimistic update
      if (context?.previousAudioNotes) {
        queryClient.setQueryData([AUDIO_NOTES_QUERY_KEY, isUsingLocalStorage], context.previousAudioNotes)
      }
      if (context?.previousSharedItems) {
        queryClient.setQueryData([SHARED_ITEMS_QUERY_KEY], context.previousSharedItems)
      }

      console.error('Failure to delete audio notes:', audioNoteIds, error)
    },
  })
}

function useUpdateAudioNoteTitle() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['update-audio-note-title'],
    mutationFn: async ({ id, title }: { id: AudioNote['id']; title: string }) => {
      await AudioNoteIpcRenderer.updateAudioNote({ id, updates: { title } })
      return { id, title }
    },
    onSuccess: async ({ id }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [AUDIO_NOTES_QUERY_KEY], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['audio-note', id] }),
      ])
      // Cross-renderer: the overlay's history feed shows this note's title.
      GlobalEvents.emit('audio-notes:changed')
    },
    onError: (error, { id }) => {
      console.error('Failure to update audio note title:', id, error)
    },
  })
}

/**
 * Metadata notes for one calendar event. Prefer this over scanning `useAudioNotes()`.
 * Invalidated with the rest of the `audio-notes` query family on save/delete.
 */
function useAudioNotesForCalendarEvent(calendarEventId: string | undefined) {
  const { getAuthToken } = useAuth()
  const { isUsingLocalStorage } = useAudioNoteSettings()

  return useQuery({
    queryKey: [AUDIO_NOTES_QUERY_KEY, 'by-calendar-event', calendarEventId, isUsingLocalStorage],
    queryFn: async (): Promise<Array<AudioNote>> => {
      if (!calendarEventId) {
        return []
      }

      if (!isUsingLocalStorage) {
        return fetchAudioNotesList(getAuthToken, {
          calendarEventId,
          view: 'metadata',
          limit: 20,
        })
      }

      const raw = await AudioNoteIpcRenderer.getAudioNotesWithinTimeWindowDays(14)
      const all = (Array.isArray(raw) ? (raw as Array<AudioNote>) : []).map(toMetadataNote)
      return all
        .filter((note) => note.meeting?.calendarEventId === calendarEventId)
        .sort((a, b) => b.endedAt.getTime() - a.endedAt.getTime())
    },
    enabled: !!calendarEventId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Today's meeting notes (metadata) for Upcoming "Earlier Today" + legacy link fallbacks.
 * Uses `startedAt[gte]=startOfToday` on the API; client still filters `endedAt` to today.
 */
function useTodaysMeetingAudioNotes(options?: { enabled?: boolean }) {
  const { getAuthToken } = useAuth()
  const { isUsingLocalStorage } = useAudioNoteSettings()
  const enabled = options?.enabled ?? true

  return useQuery({
    queryKey: [AUDIO_NOTES_QUERY_KEY, 'todays-meetings', isUsingLocalStorage],
    queryFn: async (): Promise<Array<AudioNote>> => {
      const now = Date.now()
      let notes: Array<AudioNote>

      if (!isUsingLocalStorage) {
        notes = await fetchAudioNotesList(getAuthToken, {
          view: 'metadata',
          limit: 100,
          'startedAt[gte]': startOfLocalDayIso(),
        })
      } else {
        const raw = await AudioNoteIpcRenderer.getAudioNotesWithinTimeWindowDays(1)
        notes = (Array.isArray(raw) ? (raw as Array<AudioNote>) : []).map(toMetadataNote)
      }

      return notes
        .filter((note) => {
          if (!note.meeting) {
            return false
          }
          const endedAt = new Date(note.endedAt)
          return endedAt.getTime() <= now && isDayToday(endedAt)
        })
        .sort((a, b) => b.endedAt.getTime() - a.endedAt.getTime())
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Notes linked to a calendar event. Primary path is a filtered metadata query;
 * legacy URI/title matching only runs against today's meeting notes when the
 * calendarEventId lookup is empty (avoids hydrating the full history corpus).
 */
function useLinkedAudioNotes(meeting: CalendarEvent): Array<AudioNote> {
  const { data: byEvent, isFetched: byEventFetched } = useAudioNotesForCalendarEvent(meeting?.id)
  const needsLegacyFallback = !!meeting?.id && byEventFetched && (byEvent?.length ?? 0) === 0
  const { data: todaysNotes } = useTodaysMeetingAudioNotes({ enabled: needsLegacyFallback })

  return useMemo(() => {
    if (!meeting) {
      return []
    }
    if (byEvent && byEvent.length > 0) {
      return byEvent
    }
    if (!needsLegacyFallback || !todaysNotes) {
      return byEvent ?? []
    }
    return todaysNotes.filter((audioNote) => matchesLegacyMeetingLink(audioNote, meeting))
  }, [meeting, byEvent, needsLegacyFallback, todaysNotes])
}

export {
  useAudioNotes,
  useAudioNoteById,
  useDeleteAudioNote,
  useBulkDeleteAudioNotes,
  useUpdateAudioNoteTitle,
  useLinkedAudioNotes,
  useAudioNotesForCalendarEvent,
  useTodaysMeetingAudioNotes,
}
